import { compact, sortBy } from 'lodash';

import { advancedCpmRules } from './advancedCpm';
import { audiencesRules } from './audiences';
import { findRule } from './common';
import { flightsRules } from './flights';
import { generalRules } from './general';
import { locationsRules } from './locations';
import { removeNoise } from './noise';
import { hasInformation, otherRules } from './other';
import { pricingModelRules } from './pricingModel';
import { scheduleRules } from './schedule';
import { screensRules } from './screens';
import { findSummaryEntry, summarizeChanges } from './summary';
import { venuesRules } from './venues';

import type { IntlShape } from 'react-intl';
import type { MultiFlightEntityType } from 'sonata-static-storage/lib/common/common.types';
import type { ChangelogType } from '../types';
import type { ChangelogDiff, ChangelogFormat, ChangelogRule, FormatterContext } from './types';

export { getSectionTitle, groupChangelogSections } from './sections';

export type { ChangelogSectionGroup } from './sections';
export type { ChangelogDiff, ChangelogFormat, ChangelogSection } from './types';

/** First match wins: order matters */
const ENTITY_RULES: ChangelogRule[] = [
  ...generalRules,
  ...locationsRules,
  ...flightsRules,
  ...scheduleRules,
  ...audiencesRules,
  ...pricingModelRules,
  ...advancedCpmRules,
  ...otherRules
];

function getEntityMessage(entity: MultiFlightEntityType, formatMessage: IntlShape['formatMessage']): string {
  return {
    campaignPlan: formatMessage({ defaultMessage: 'Campaign Plan', id: 'changelogModal.message.cp' }),
    insertionOrder: formatMessage({ defaultMessage: 'Insertion Order', id: 'changelogModal.message.io' }),
    lineItem: formatMessage({ defaultMessage: 'Line Item', id: 'changelogModal.message.li' })
  }[entity];
}

function formatWithRules(
  changes: ChangelogDiff[],
  rules: ChangelogRule[],
  context: FormatterContext
): ChangelogFormat[] {
  return changes.flatMap(change => {
    const rule = findRule(rules, change);
    if (!rule || (rule.section === 'other' && !hasInformation(change))) {
      return [];
    }
    return { description: rule.format(change, context), order: rule.order, section: rule.section };
  });
}

function formatVenues(changes: ChangelogDiff[], context: FormatterContext): ChangelogFormat[] {
  const unknownChanges = changes.filter(change => !findSummaryEntry(venuesRules, change, context));
  return [...summarizeChanges(changes, venuesRules, context), ...formatWithRules(unknownChanges, otherRules, context)];
}

/** Formats the raw deep-diff changes of a changelog document, sorted by order */
export function getChangeLogData(
  changes: ChangelogDiff[],
  entity: MultiFlightEntityType,
  changeType: ChangelogType,
  formatMessage: IntlShape['formatMessage']
): ChangelogFormat[] {
  const context: FormatterContext = { entity: getEntityMessage(entity, formatMessage), formatMessage };
  const diffs: ChangelogDiff[] = removeNoise(compact(changes));
  if (changeType === 'screen') {
    return summarizeChanges(diffs, screensRules, context);
  }
  if (changeType === 'venue') {
    return sortBy(formatVenues(diffs, context), 'order');
  }
  return sortBy(formatWithRules(diffs, ENTITY_RULES, context), 'order');
}
